import { ACTOR_ROLES, type ActorRole } from './enums.js';

/**
 * Role → capability, once, for both ends of the wire.
 *
 * `MeResponse` deliberately carries roles and no permission list: the server is
 * the only authority on what a caller may do, and shipping a client-supplied
 * permission array would invite a UI that believes it. But the UI still has to
 * decide whether to render the Publish button, and the only honest way to do
 * that without a second source of truth is for both sides to read the same
 * table. So this is it — the API's `Role` value object is pinned to it by a
 * spec (`apps/api/src/shared/domain/role.capabilities.contract.spec.ts`), and
 * the web app derives every affordance from it.
 *
 * What this is NOT: authorisation. Nothing here is enforcement. Hiding a
 * control is a courtesy to the user; the guard on the endpoint is the security
 * boundary, and it re-derives the same answer server-side on every request.
 */

export const CAPABILITIES = [
  'method:read',
  'method:author',
  'method:submit',
  'method:review',
  'method:publish',
  'method:import',
  'catalog:manage',
  /**
   * Write to *one adoption's* catalogue — the entries a client brings into the
   * session they are tailoring in.
   *
   * Separate from `catalog:manage` because the blast radius is different, not
   * because the work is. `catalog:manage` edits the platform catalogue that every
   * method and every client sees; this edits rows that exist only inside one
   * session and appear in no other palette. That is why a facilitator holds it and
   * does not hold `catalog:manage`: running the room they are in is their job, and
   * re-authoring the platform's tool list is not.
   */
  'catalog:manageSession',
  'deliverable:manage',
  'attachment:upload',
  'workshop:facilitate',
  'workshop:publishVariant',
  'plan:manage',
  'assist:invoke',
  'audit:read',
  'admin:manage',
] as const;

export type Capability = (typeof CAPABILITIES)[number];

const VIEWER: readonly Capability[] = ['method:read'];

const METHOD_AUTHOR: readonly Capability[] = [
  'method:read',
  'method:author',
  'method:submit',
  'method:import',
  'catalog:manage',
  // An author who may rewrite the platform catalogue may certainly add an entry to
  // one session's — the narrower grant follows from the wider one.
  'catalog:manageSession',
  'deliverable:manage',
  'attachment:upload',
  'plan:manage',
  'assist:invoke',
];

/**
 * A reviewer decides, and needs the two things deciding takes: evidence
 * (`attachment:upload` — the annotated screenshot of the gate that is wrong)
 * and "summarise what changed since the last revision" (`assist:invoke`).
 * Deliberately still no `method:author` or `method:submit`: the reviewer who
 * can edit what they are approving is the separation of duties the role exists
 * to create.
 */
const REVIEWER: readonly Capability[] = [
  'method:read',
  'method:review',
  'method:publish',
  'attachment:upload',
  'assist:invoke',
];

/**
 * `plan:manage` sits with the facilitator and the author, and with nobody else.
 *
 * A plan is what an adoption is *for* — the room that tailored the method is the room that
 * turns it into a work breakdown, and an author planning their own method is how the
 * durations and the training list get sanity-checked before a client sees them. A reviewer
 * deliberately has none of it: approving a method and scheduling somebody's next six months
 * are different jobs, and a viewer reads plans through `method:read` like everything else.
 */
const WORKSHOP_FACILITATOR: readonly Capability[] = [
  'method:read',
  'workshop:facilitate',
  'workshop:publishVariant',
  // The catalogue of the room they are running, and not the platform's. A client
  // brings its own tools to an adoption workshop and the facilitator is who is in
  // the room to enter them.
  'catalog:manageSession',
  'deliverable:manage',
  'attachment:upload',
  'plan:manage',
  'assist:invoke',
];

export const ROLE_CAPABILITIES: Readonly<Record<ActorRole, readonly Capability[]>> = Object.freeze({
  ADMIN: CAPABILITIES,
  METHOD_AUTHOR,
  REVIEWER,
  WORKSHOP_FACILITATOR,
  VIEWER,
});

/** Roles are additive: a user with two roles holds the union of their grants. */
export const capabilitiesOf = (roles: readonly ActorRole[]): ReadonlySet<Capability> => {
  const granted = new Set<Capability>();
  for (const role of roles) {
    for (const capability of ROLE_CAPABILITIES[role] ?? []) granted.add(capability);
  }
  return granted;
};

export const roleHasCapability = (role: ActorRole, capability: Capability): boolean =>
  ROLE_CAPABILITIES[role].includes(capability);

export const isCapability = (value: string): value is Capability =>
  (CAPABILITIES as readonly string[]).includes(value);

/**
 * Every capability is reachable by some non-ADMIN role, or it is a capability
 * only the owner can ever exercise. Both are legitimate; this records which is
 * which so a new capability cannot be added and then silently granted to
 * nobody. `admin:manage` and `audit:read` are ADMIN-only by design.
 */
export const ADMIN_ONLY_CAPABILITIES: readonly Capability[] = CAPABILITIES.filter(
  (capability) =>
    !ACTOR_ROLES.filter((role) => role !== 'ADMIN').some((role) =>
      roleHasCapability(role, capability),
    ),
);
